import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './OktaLoginPage.css';
import Header from './Header';
import { useOktaAuth } from '@okta/okta-react';
import { useAuth } from '../contexts/AuthContext';

// sessionStorage key used as a cross-remount guard to prevent concurrent PKCE flows.
// A component-local ref resets on every unmount, which can trigger duplicate
// signInWithRedirect calls (e.g. StrictMode double-invoke, back-navigation remounts)
// and overwrite the PKCE code verifier stored by the first flow, causing:
// "AuthSdkError: Could not load PKCE codeVerifier from storage"
const PKCE_REDIRECT_GUARD_KEY = 'okta_pkce_redirect_in_progress';

function OktaLoginPage() {
  const navigate = useNavigate();
  const { oktaAuth, authState } = useOktaAuth();
  const { error: authError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  // True when a prior redirect stored the guard but the user navigated back without completing auth
  const [redirectBlocked, setRedirectBlocked] = useState(false);

  // Derive display error - prefer authError from context, fallback to local error
  const errorMessage = authError || localError;
  const isAuthContextError = Boolean(authError);

  // Define the login handler
  const handleOktaLogin = useCallback(async () => {
    // Clear any stale guard from a previous (possibly abandoned) redirect before
    // setting a fresh one.  This is also the explicit retry path.
    sessionStorage.removeItem(PKCE_REDIRECT_GUARD_KEY);
    setRedirectBlocked(false);
    setLocalError('');
    setIsLoading(true);
    try {
      // Mark that a PKCE redirect is in progress so re-mounts don't start another
      sessionStorage.setItem(PKCE_REDIRECT_GUARD_KEY, '1');
      // Use app base path so Security.restoreOriginalUri can route correctly
      const base = import.meta.env.BASE_URL || '/';
      await oktaAuth.signInWithRedirect({ originalUri: base });
    } catch (err) {
      sessionStorage.removeItem(PKCE_REDIRECT_GUARD_KEY);
      console.error('Okta sign-in redirect failed:', err);
      setLocalError('Failed to initiate Okta sign-in. Please try again.');
      setIsLoading(false);
    }
  }, [oktaAuth]);

  // Log AuthContext errors when they appear
  useEffect(() => {
    if (authError) {
      console.log('OktaLoginPage: Displaying AuthContext error:', authError);
      // Guard is no longer needed — clear it so a retry can proceed
      sessionStorage.removeItem(PKCE_REDIRECT_GUARD_KEY);
    }
  }, [authError]);

  useEffect(() => {
    // If there's an AuthContext error, stay here to display it
    if (authError) {
      return;
    }
    // If already authenticated, clear the guard and navigate away
    if (authState?.isAuthenticated) {
      sessionStorage.removeItem(PKCE_REDIRECT_GUARD_KEY);
      navigate('/', { replace: true });
      return;
    }

    // Automatically redirect to Okta login when component mounts (no error present)
    if (authState && !authState.isAuthenticated && !authError) {
      if (!sessionStorage.getItem(PKCE_REDIRECT_GUARD_KEY)) {
        // No in-progress redirect — start one
        handleOktaLogin();
      } else {
        // Guard is set but we're back on the login page (user likely pressed Back
        // from the Okta sign-in page). Surface a manual button so they can proceed.
        setRedirectBlocked(true);
      }
    }
  }, [authState, authError, navigate, handleOktaLogin]);

  return (
    <>
      <Header />
      <div className="okta-login-container" data-testid="okta-login-page">
        <h2 className="okta-login-title">
          {errorMessage ? 'Login Failed' : 'Redirecting to Okta...'}
        </h2>
        {errorMessage && (
          <>
            <div className="okta-error-message" role="alert">{errorMessage}</div>
            {!isAuthContextError ? (
              // Show retry button only for redirect failures, not backend errors
              <button 
                className="okta-retry-button" 
                onClick={handleOktaLogin}
                disabled={isLoading}
              >
                {isLoading ? 'Redirecting...' : 'Retry Login'}
              </button>
            ) : (
              // For backend/auth errors, show support message instead
              <p className="okta-support-message">
                Please contact support if this issue persists.
              </p>
            )}
          </>
        )}
        {/* User navigated back from Okta's sign-in page — previous redirect guard is
            still set, so auto-redirect was blocked. Show a manual continue button. */}
        {redirectBlocked && !errorMessage && !isLoading && (
          <button
            className="okta-retry-button"
            onClick={handleOktaLogin}
          >
            Continue to Okta Login
          </button>
        )}
      </div>
    </>
  );
}

export default OktaLoginPage;
